import React, { useState, useMemo } from 'react';
import { X, Mail, Search, Loader } from 'lucide-react';
import API_URL from '../../config';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function CalendarEmailModal({ users, printYear, printStartMonth, printEndMonth, getAuthHeader, onClose }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState(`Papa's Palace — Reservation Calendar ${printYear}`);
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState(null);

  const activeUsers = useMemo(
    () => users.filter(u => u.is_active !== false),
    [users]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return activeUsers;
    const q = search.trim().toLowerCase();
    return activeUsers.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [activeUsers, search]);

  const toggleUser = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filtered.map(u => u.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedIds.size) {
      setError('Select at least one recipient.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/calendar/email-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          year: printYear,
          startMonth: printStartMonth,
          endMonth: printEndMonth,
          recipientUserIds: Array.from(selectedIds),
          subject: subject.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to queue email.');
        return;
      }
      setQueued(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const rangeLabel = `${MONTH_NAMES[printStartMonth]} ${printYear} – ${MONTH_NAMES[printEndMonth]} ${printYear}`;

  return (
    <div className="approval-modal-overlay" onClick={onClose}>
      <div className="approval-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="approval-modal-header">
          <div>
            <h3 className="approval-modal-title">Email Reservation Calendar</h3>
            <p className="approval-modal-subtitle">{rangeLabel}</p>
          </div>
          <button className="approval-modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {queued ? (
          <div className="approval-modal-body" style={{ padding: '24px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#27ae60', fontWeight: 600 }}>
              <Mail size={20} />
              Email queued — recipients will receive the calendar shortly.
            </div>
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button className="btn btn-neutral" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="approval-modal-body">
              <div className="approval-modal-section">
                <label className="approval-modal-label">
                  Recipients ({selectedIds.size} selected)
                </label>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px 6px 28px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ fontSize: 12, marginBottom: 6, display: 'flex', gap: 12 }}>
                  <button type="button" className="approval-modal-hint" style={{ background: 'none', border: 'none', padding: 0, color: '#2980b9', cursor: 'pointer', fontSize: 12 }} onClick={selectAll}>
                    Select all ({filtered.length})
                  </button>
                  <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: '#888', cursor: 'pointer', fontSize: 12 }} onClick={deselectAll}>
                    Deselect all
                  </button>
                </div>
                <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
                  {filtered.length === 0 && (
                    <div style={{ padding: '12px 10px', color: '#999', fontSize: 13 }}>No users found.</div>
                  )}
                  {filtered.map(u => (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        onChange={() => toggleUser(u.id)}
                        style={{ flexShrink: 0 }}
                      />
                      <span style={{ fontWeight: 500 }}>{u.name}</span>
                      <span style={{ color: '#888', marginLeft: 'auto', fontSize: 12 }}>{u.email}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="approval-modal-section" style={{ marginTop: 16 }}>
                <label className="approval-modal-label">Subject (optional)</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="approval-modal-select"
                  style={{ width: '100%' }}
                />
              </div>

              {error && (
                <div className="approval-modal-error" style={{ marginTop: 12 }}>
                  {error}
                </div>
              )}
            </div>

            <div className="approval-modal-footer">
              <button type="button" className="btn btn-neutral" onClick={onClose} disabled={sending}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={sending || !selectedIds.size}>
                {sending ? <><Loader size={14} className="template-preview-spinner" /> Sending...</> : <><Mail size={14} /> Send PDF via Email</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CalendarEmailModal;
